import React, { useState, useEffect } from 'react';
import { Card, Collapse, IconButton, Grid, Divider, TableCell, TableRow } from '@mui/material';
import ArgonBox from 'components/ArgonBox';
import ArgonTypography from 'components/ArgonTypography';
import StageExpandableTable from '../tabels/StageExpandableTable';
import Icon from '@mui/material/Icon';
import { grey, red, green } from '@mui/material/colors';
import PropTypes from 'prop-types';
import ProductList from './ProductList';

import { useVisibility } from 'context/visibilityContext';
import { filterDiffsByDisplayColumns } from 'utils/visibilityFilter';
import DiffRenderer from './DiffRenderer';

// This component is a direct replacement for PricingModelReview
function PricingModelReview({ selectedOrg, productData, showSameFields }) {
  const [expandedRow, setExpandedRow] = useState(null);
  const [productDataMap, setProductDataMap] = useState({});
  const [selectedProductId, setSelectedProductId] = useState(null);

  const { visibilityConfig } = useVisibility();

  // A single, generic column definition for all our tables
  const columns = [
    { name: 'item', align: 'left' },
    { name: 'actions', align: 'right', width: '50px' },
  ];

  useEffect(() => {
    if (!productData || productData.length === 0) {
      setProductDataMap({});
      return;
    }

    const generatedProductDataMap = {};

    // Helper to process each pricing array (dimensions, blocks, costs)
    const processPricingArray = (diffArray, sObjectName) => {
      const result = { new: [], changed: [], deleted: [], same: [] };
      ['new', 'changed', 'deleted', 'same'].forEach((type) => {
        (diffArray[type] || []).forEach((item) => {
          // The actual data might be nested inside a wrapper key
          const itemData = item.priceDimensions || item.blockPrices || item;
          result[type].push({
            id: itemData.sfId || itemData.id,
            // Display name, with a fallback for items like costs that may not have one
            item: (
              <ArgonTypography variant="caption" color="secondary" fontWeight="bold">
                {itemData.name || `Item - ${itemData.sfId}`}
              </ArgonTypography>
            ),
            diffs: item.changes,
            sObjectName: sObjectName, // Pass the sObject name for filtering
            type: type,
          });
        });
      });
      return result;
    };

    productData.forEach((productDiff) => {
      const pricingModel = productDiff.pricingModel || {};

      generatedProductDataMap[productDiff.product.sfId || productDiff.product.id] = {
        priceDimensions: processPricingArray(pricingModel.priceDimensions || {}, 'SBQQ__PriceDimension__c'),
        blockPrices: processPricingArray(pricingModel.blockPrices || {}, 'SBQQ__BlockPrice__c'),
        costs: processPricingArray(pricingModel.costs || {}, 'SBQQ__Cost__c'),
        productName: productDiff.product.name,
      };
    });

    setProductDataMap(generatedProductDataMap);
    if (productData.length > 0) {
      setSelectedProductId(productData[0].product.sfId || productData[0].product.id);
    }
  }, [productData]);

  const handleRowClick = (rowId) => {
    setExpandedRow((prev) => (prev === rowId ? null : rowId));
  };

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
  };

  const renderExpandedContent = (row) => (
    <TableRow style={{ width: '100%', padding: '0px' }}>
      <TableCell colSpan={columns.length} style={{ padding: '0px' }}>
        <Collapse in={expandedRow === row.id} timeout="auto" unmountOnExit style={{ width: '100%' }}>
          <ArgonBox p={3} color="text" bgcolor={grey[50]}>
            <DiffRenderer
              changes={row.diffs}
              sObjectName={row.sObjectName}
              orgType={selectedOrg.type.toLowerCase()}
              showSameFields={showSameFields}
            />
          </ArgonBox>
        </Collapse>
      </TableCell>
    </TableRow>
  );

  const renderProductSection = (productName, productData) => {
    const { priceDimensions, blockPrices, costs } = productData;

    const renderTableSection = (title, rows, renderRowSubComponent) => {
      if (!rows || rows.length === 0) return null;
      return (
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                {title}
              </ArgonTypography>
              <ArgonBox
                sx={{
                  '& .MuiTableRow-root:not(:last-child)': {
                    '& td': {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                <StageExpandableTable
                  rows={rows}
                  columns={columns}
                  renderRowSubComponent={({ row }) => renderRowSubComponent(row)}
                />
              </ArgonBox>
            </ArgonBox>
          </Card>
        </ArgonBox>
      );
    };

    const formattedRows = (rows) =>
      rows.map((row) => ({
        ...row,
        onClick: () => handleRowClick(row.id),
        selected: expandedRow === row.id,
        actions: (
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleRowClick(row.id);
            }}
          >
            <Icon>{expandedRow === row.id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}</Icon>
          </IconButton>
        ),
      }));

    const modelSections = [
      { title: 'Price Dimensions', data: priceDimensions },
      { title: 'Block Prices', data: blockPrices },
      { title: 'Costs', data: costs },
    ];

    const hasAnyData = modelSections.some((section) =>
      ['new', 'changed', 'deleted', 'same'].some((type) => section.data[type] && section.data[type].length > 0)
    );

    if (!hasAnyData) {
      return (
        <Card>
          <ArgonBox p={3}>
            <ArgonTypography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
              No pricing model data available for this product.
            </ArgonTypography>
          </ArgonBox>
        </Card>
      );
    }

    return (
      <ArgonBox key={productName} mb={3}>
        {modelSections.map((section) => (
          <React.Fragment key={section.title}>
            {['new', 'changed', 'deleted', 'same'].map(
              (type) =>
                section.data[type] &&
                section.data[type].length > 0 &&
                renderTableSection(
                  `${type.charAt(0).toUpperCase() + type.slice(1)} ${section.title}`,
                  formattedRows(section.data[type]),
                  (row) => renderExpandedContent(row)
                )
            )}
          </React.Fragment>
        ))}
      </ArgonBox>
    );
  };

  const productList = Object.keys(productDataMap).map((productId) => ({
    id: productId,
    name: productDataMap[productId].productName,
  }));

  return (
    <ArgonBox>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <ProductList
            products={productList}
            selectedProductId={selectedProductId}
            onProductSelect={handleProductSelect}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          {selectedProductId && productDataMap[selectedProductId] ? (
            renderProductSection(productDataMap[selectedProductId].productName, productDataMap[selectedProductId])
          ) : (
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
                  Select a product to view its pricing model.
                </ArgonTypography>
              </ArgonBox>
            </Card>
          )}
        </Grid>
      </Grid>
    </ArgonBox>
  );
}

PricingModelReview.propTypes = {
  productData: PropTypes.arrayOf(
    PropTypes.shape({
      pricingModel: PropTypes.shape({
        priceDimensions: PropTypes.object,
        blockPrices: PropTypes.object,
        costs: PropTypes.object,
      }),
      product: PropTypes.shape({
        name: PropTypes.string,
        sfId: PropTypes.string,
        id: PropTypes.string,
      }),
    })
  ).isRequired,
  showSameFields: PropTypes.bool,
  selectedOrg: PropTypes.object.isRequired,
};

export default PricingModelReview;
